import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { ImageFieldWithPicker } from '@/components/admin/AssetPickerDialog';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { 
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Video,
  FileText,
  Download,
  CheckSquare,
  Filter,
  MoreVertical,
  ExternalLink,
  Database,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CONTENT_TYPES = [
  { id: 'article', name: 'Article', icon: FileText },
  { id: 'video', name: 'Video', icon: Video },
  { id: 'pdf', name: 'PDF/Download', icon: Download },
  { id: 'checklist', name: 'Checklist', icon: CheckSquare }
];

const AdminResourceManager = () => {
  // State
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    content_type: 'article',
    category_id: '',
    content: '',
    video_url: '',
    file_url: '',
    thumbnail_url: '',
    tags: '',
    country: '',
    reading_time: '',
    is_featured: false,
    is_published: true
  });

  useEffect(() => {
    fetchData();
  }, [filterCategory, filterType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch categories
      const catRes = await fetch(`${API_URL}/api/resources/categories`);
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data.categories || []);
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/resources/admin/stats`);
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      // Fetch resources
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterType) params.append('content_type', filterType);
      params.append('limit', '50');

      const resourceRes = await fetch(`${API_URL}/api/resources/admin/all?${params}`);
      if (resourceRes.ok) {
        const data = await resourceRes.json();
        setResources(data.resources || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.description || !form.category_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim().toLowerCase()) : [],
        reading_time: form.reading_time ? parseInt(form.reading_time) : null
      };

      const url = editingResource 
        ? `${API_URL}/api/resources/${editingResource.resource_id}`
        : `${API_URL}/api/resources`;
      
      const method = editingResource ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingResource ? 'Resource updated' : 'Resource created');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save resource');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save resource');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      const res = await fetch(`${API_URL}/api/resources/${resourceId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Resource deleted');
        fetchData();
      } else {
        toast.error('Failed to delete resource');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete resource');
    }
  };

  const handleTogglePublish = async (resource) => {
    try {
      const res = await fetch(`${API_URL}/api/resources/${resource.resource_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !resource.is_published })
      });

      if (res.ok) {
        toast.success(resource.is_published ? 'Resource unpublished' : 'Resource published');
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const handleToggleFeatured = async (resource) => {
    try {
      const res = await fetch(`${API_URL}/api/resources/${resource.resource_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_featured: !resource.is_featured })
      });

      if (res.ok) {
        toast.success(resource.is_featured ? 'Removed from featured' : 'Added to featured');
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing resources with seed data. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/resources/admin/seed`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Resources seeded successfully!');
        fetchData();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const openEditModal = (resource) => {
    setEditingResource(resource);
    setForm({
      title: resource.title || '',
      description: resource.description || '',
      content_type: resource.content_type || 'article',
      category_id: resource.category_id || '',
      content: resource.content || '',
      video_url: resource.video_url || '',
      file_url: resource.file_url || '',
      thumbnail_url: resource.thumbnail_url || '',
      tags: resource.tags?.join(', ') || '',
      country: resource.country || '',
      reading_time: resource.reading_time?.toString() || '',
      is_featured: resource.is_featured || false,
      is_published: resource.is_published !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingResource(null);
    setForm({
      title: '',
      description: '',
      content_type: 'article',
      category_id: '',
      content: '',
      video_url: '',
      file_url: '',
      thumbnail_url: '',
      tags: '',
      country: '',
      reading_time: '',
      is_featured: false,
      is_published: true
    });
  };

  const getTypeIcon = (type) => {
    const typeConfig = CONTENT_TYPES.find(t => t.id === type);
    return typeConfig?.icon || FileText;
  };

  const filteredResources = resources.filter(r => 
    !searchQuery || 
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resource Library</h1>
            <p className="text-gray-500">Manage articles, videos, and guides</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSeedData} disabled={seeding}>
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Seed Data
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Resources</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-green-600">{stats.published}</p>
                <p className="text-sm text-gray-500">Published</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
                <p className="text-sm text-gray-500">Drafts</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-violet-600">{stats.featured}</p>
                <p className="text-sm text-gray-500">Featured</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-2xl font-bold">{stats.total_views?.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Views</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {CONTENT_TYPES.map(type => (
                <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Resources List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No resources found</p>
            <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowModal(true); }}>
              Create your first resource
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredResources.map((resource) => {
              const TypeIcon = getTypeIcon(resource.content_type);
              return (
                <div 
                  key={resource.resource_id}
                  className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    resource.content_type === 'video' ? 'bg-rose-100 text-rose-600' :
                    resource.content_type === 'pdf' ? 'bg-emerald-100 text-emerald-600' :
                    'bg-blue-100 text-blue-600'
                  }`}>
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">{resource.title}</h3>
                      {resource.is_featured && (
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      )}
                      {!resource.is_published && (
                        <Badge variant="outline" className="text-xs">Draft</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{resource.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span className="capitalize">{resource.category_id?.replace(/-/g, ' ')}</span>
                      <span>{resource.views || 0} views</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleTogglePublish(resource)}
                      title={resource.is_published ? 'Unpublish' : 'Publish'}
                    >
                      {resource.is_published ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditModal(resource)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleFeatured(resource)}>
                          <Star className="w-4 h-4 mr-2" />
                          {resource.is_featured ? 'Remove from Featured' : 'Add to Featured'}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => window.open(`/uac-desk/resources/${resource.slug || resource.resource_id}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(resource.resource_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingResource ? 'Edit Resource' : 'Create Resource'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Resource title"
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Brief description"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Content Type *</Label>
                  <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(form.content_type === 'article' || form.content_type === 'checklist') && (
                  <div className="col-span-2">
                    <Label>Content (HTML)</Label>
                    <Textarea
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      placeholder="<p>Article content here...</p>"
                      rows={8}
                      className="mt-1 font-mono text-sm"
                    />
                  </div>
                )}
                
                {form.content_type === 'video' && (
                  <div className="col-span-2">
                    <Label>Video URL (YouTube/Vimeo embed)</Label>
                    <Input
                      value={form.video_url}
                      onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                      placeholder="https://www.youtube.com/embed/..."
                      className="mt-1"
                    />
                  </div>
                )}
                
                {form.content_type === 'pdf' && (
                  <div className="col-span-2">
                    <Label>File URL</Label>
                    <Input
                      value={form.file_url}
                      onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                      placeholder="https://example.com/file.pdf"
                      className="mt-1"
                    />
                  </div>
                )}
                
                <div className="col-span-2">
                  <ImageFieldWithPicker
                    label="Thumbnail"
                    value={form.thumbnail_url}
                    onChange={(url) => setForm({ ...form, thumbnail_url: url })}
                    onClear={() => setForm({ ...form, thumbnail_url: '' })}
                    uploadCategory="blog"
                    uploadAssetKeyPrefix="resource_thumb"
                    placeholder="Select or paste thumbnail URL"
                  />
                </div>
                
                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    placeholder="visa, usa, tips"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Country (optional)</Label>
                  <Input
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="USA, UK, etc."
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Reading Time (minutes)</Label>
                  <Input
                    type="number"
                    value={form.reading_time}
                    onChange={(e) => setForm({ ...form, reading_time: e.target.value })}
                    placeholder="5"
                    className="mt-1"
                  />
                </div>
                
                <PublishStatusCard
                  isPublished={form.is_published}
                  onChange={(v) => setForm({ ...form, is_published: v })}
                  entityLabel="resource"
                  publicRoute="/resources"
                  testId="resource-publish-toggle"
                />

                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(v) => setForm({ ...form, is_featured: v })}
                  />
                  <Label>Featured</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave}>
                {editingResource ? 'Update' : 'Create'} Resource
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminResourceManager;
